import React from 'react';
import styled from 'styled-components';
import { useIntl } from 'react-intl';
import { JourneyResults } from './glp1Indicators';
import { BAND_COLORS } from './scoreBands';
import { colors, fontSize, fontWeight, spacing } from '../../style/tokens';
import {
  BANNER_KEY,
  JOURNEY_TITLE_KEY,
  UNAVAILABLE_KEY,
  INDICATOR_LABEL_KEYS,
  WEIGHT_JOURNEY_KEYS,
  TOLERABILITY_KEYS,
  MUSCLE_KEYS,
  PRESCRIBER_CUE_KEY,
} from './glp1Copy';

/**
 * The GLP-1 Journey panel (§4.11–§4.13, §7).
 *
 * Rendered in a section VISUALLY SEPARATE from the Wellness Snapshot and never
 * merged into its number — the three indicators are excluded from the Snapshot
 * (§5), and the separation is what stops them reading as part of it.
 *
 * Nothing here may be presented as medication efficacy, treatment response,
 * side-effect detection or a reason to change a dose (§2, §6). The mandatory
 * banner is not optional chrome: §2 requires it on every screen showing a
 * Journey element.
 *
 * Presentational — the caller computes the results (see computeJourney) and
 * decides whether the panel renders at all (see glp1Active).
 */

// Tone maps each indicator's own bands onto the shared three-colour palette.
// Colour is presentation; the label text always states the protocol's band.
type Tone = keyof typeof BAND_COLORS;

const WEIGHT_TONE: Record<string, Tone> = {
  steady: 'normal',
  onTrack: 'normal',
  gettingStarted: 'noting',
  slightRegain: 'noting',
  veryRapid: 'outside',
};
const TOLERABILITY_TONE: Record<string, Tone> = {
  comfortable: 'normal',
  someDiscomfort: 'noting',
  persistentDiscomfort: 'outside',
};
const MUSCLE_TONE: Record<string, Tone> = {
  wellProtected: 'normal',
  roomToStrengthen: 'noting',
  higherAttention: 'outside',
};

const Section = styled.div`
  padding: 0 14px;
  margin-bottom: ${spacing.lg};
`;

const SectionTitle = styled.h2`
  font-size: ${fontSize.xs};
  font-weight: ${fontWeight.bold};
  letter-spacing: 0.08em;
  text-transform: uppercase;
  color: ${p => p.theme.gradient1};
  margin-bottom: 10px;
  padding: 0 2px;
`;

// A dashed edge and its own heading keep the panel visibly apart from the
// Snapshot card above it (§7 "visually separate from the Wellness Snapshot").
const Card = styled.div`
  background: ${colors.bg};
  border: 1.5px dashed ${p => p.theme.gradient1};
  border-radius: 14px;
  padding: 4px 18px;
`;

const Row = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  padding: 13px 0;
  border-bottom: 1px solid #f5f5f5;
  &:last-child { border-bottom: none; }
`;

const RowMain = styled.div`
  min-width: 0;
`;

const Name = styled.div`
  font-size: ${fontSize.base};
  font-weight: ${fontWeight.semibold};
  color: ${colors.text};
`;

const BandText = styled.div<{ $tone: Tone }>`
  font-size: ${fontSize.sm};
  color: ${colors.muted};
  margin-top: 3px;
  display: flex;
  align-items: center;
  gap: 6px;
  &::before {
    content: '';
    width: 8px;
    height: 8px;
    border-radius: 50%;
    flex-shrink: 0;
    background: ${({ $tone }) => BAND_COLORS[$tone]};
  }
`;

const Score = styled.div`
  font-size: ${fontSize.xl};
  font-weight: ${fontWeight.extrabold};
  color: ${colors.navy};
  flex-shrink: 0;
`;

const Unavailable = styled.div`
  font-size: ${fontSize.xl};
  font-weight: ${fontWeight.bold};
  color: #ccc;
  flex-shrink: 0;
`;

// The prescriber escalation is the one cue that must be impossible to miss.
const Escalation = styled.div`
  margin-top: 10px;
  padding: 12px 14px;
  border-radius: 12px;
  background: #fff1ec;
  border: 1.5px solid ${BAND_COLORS.outside};
  font-size: ${fontSize.base};
  font-weight: ${fontWeight.semibold};
  color: #8a3216;
`;

const SoftCue = styled.div`
  margin-top: 8px;
  font-size: ${fontSize.sm};
  color: ${colors.muted};
  line-height: 1.5;
`;

const Banner = styled.p`
  margin-top: 10px;
  padding: 0 2px;
  font-size: ${fontSize.xs};
  color: ${colors.muted};
  line-height: 1.55;
`;

interface JourneyRowProps {
  nameKey: string;
  available: boolean;
  score: number;
  bandKey: string;
  tone: Tone;
}

const JourneyRow: React.FC<JourneyRowProps> = ({ nameKey, available, score, bandKey, tone }) => {
  const intl = useIntl();
  return (
    <Row>
      <RowMain>
        <Name>{intl.formatMessage({ id: nameKey })}</Name>
        <BandText $tone={available ? tone : 'noting'}>
          {intl.formatMessage({ id: available ? bandKey : UNAVAILABLE_KEY })}
        </BandText>
      </RowMain>
      {available ? <Score>{score}</Score> : <Unavailable>–</Unavailable>}
    </Row>
  );
};

const JourneyPanel: React.FC<{ journey: JourneyResults }> = ({ journey }) => {
  const intl = useIntl();
  const { weightJourney, tolerability, muscle } = journey;

  // §4.12 escalation precedence: when the prescriber message fires, the softer
  // band label is SUPPRESSED, so the screen never shows "hydrate & pace meals"
  // and "contact your prescriber" at once.
  const escalated = tolerability.available && tolerability.suppressSoftBand;
  const tolerabilityBandKey = escalated
    ? PRESCRIBER_CUE_KEY
    : TOLERABILITY_KEYS[tolerability.band];

  // The soft HR cue only ever shows when nothing escalated (scoreTolerability
  // already enforces this; the check keeps the rule visible at the render site).
  // cueKey is already an i18n key — the scorers emit them directly.
  const softCueKey = !escalated ? tolerability.cueKey : null;

  return (
    <Section>
      <SectionTitle>{intl.formatMessage({ id: JOURNEY_TITLE_KEY })}</SectionTitle>
      <Card>
        <JourneyRow
          nameKey={INDICATOR_LABEL_KEYS.weightJourney}
          available={weightJourney.available}
          score={weightJourney.score}
          bandKey={WEIGHT_JOURNEY_KEYS[weightJourney.band]}
          tone={WEIGHT_TONE[weightJourney.band]}
        />
        <JourneyRow
          nameKey={INDICATOR_LABEL_KEYS.tolerability}
          available={tolerability.available}
          score={tolerability.score}
          bandKey={tolerabilityBandKey}
          tone={escalated ? 'outside' : TOLERABILITY_TONE[tolerability.band]}
        />
        <JourneyRow
          nameKey={INDICATOR_LABEL_KEYS.muscle}
          available={muscle.available}
          score={muscle.score}
          bandKey={MUSCLE_KEYS[muscle.band]}
          tone={MUSCLE_TONE[muscle.band]}
        />
      </Card>

      {escalated && (
        <Escalation>{intl.formatMessage({ id: PRESCRIBER_CUE_KEY })}</Escalation>
      )}
      {softCueKey && <SoftCue>{intl.formatMessage({ id: softCueKey })}</SoftCue>}

      <Banner>{intl.formatMessage({ id: BANNER_KEY })}</Banner>
    </Section>
  );
};

export default JourneyPanel;
